import "./notificationCampaigns.scss";
import "assets/styles/table.scss";
// `.home-stat` / `.home-stats-grid` — the dashboard's figure tile. Imported as
// a module rather than @import-ed, so webpack emits it once however many
// screens use it.
import "components/HomePage/home.scss";
import { useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { Form } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import { Ring } from "@uiball/loaders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUsers,
    faMobileScreenButton,
    faPaperPlane,
    faUserSlash,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { BRAND } from "constants/brand";
import { ROLE_PLURAL_LABELS } from "constants/roleLabels";
import SelectField from "components/Ui/SelectField/SelectField";
import TableBodyCells from "components/Ui/TableBodyCells/TableBodyCells";
import TableColumnsMenu from "components/Ui/TableColumnsMenu/TableColumnsMenu";
import TableHead from "components/Ui/TableHead/TableHead";
import useCity from "hooks/useCity";
import useTableColumnsConfig from "hooks/useTableColumnsConfig";
import { useGetUsersDropdown } from "hooks/useUser";
import {
    useCreateNotificationCampaign,
    useNotificationCampaignReach,
    useNotificationCampaigns,
} from "hooks/useNotificationCampaigns";
import {
    numberFormatter,
    notificationStatusLabel,
    notificationTargetLabel,
} from "./notificationCampaignView";
import { formatDateTime } from "utils/dateFormat";

const MODE_OPTIONS = [
    { value: "audience", label: "مجموعة حسابات" },
    { value: "user", label: "حساب محدد" },
];

const ROLE_OPTIONS = [
    { value: "customer", label: ROLE_PLURAL_LABELS.customer },
    { value: "vendor", label: ROLE_PLURAL_LABELS.vendor },
    { value: "delivery", label: ROLE_PLURAL_LABELS.delivery },
];

const ACTIVITY_OPTIONS = [
    { value: "all", label: "كل الحسابات النشطة" },
    { value: "ordered_within", label: "طلبوا خلال مدة" },
    { value: "never_ordered", label: "لم يطلبوا من قبل" },
];

/**
 * THE TABLE ANSWERS FOUR QUESTIONS: when, to whom, what, and how it went.
 * It used to spend three columns on الحسابات / تم قبوله / الفجوة — three bare
 * numbers that only mean anything next to each other — so they are one
 * النتيجة cell that reads as a sentence. The campaign page has the breakdown.
 */
const CAMPAIGN_COLUMNS = [
    { id: "id", label: "#", width: "64px", pinned: true },
    { id: "createdAt", label: "التاريخ", width: "165px" },
    { id: "target", label: "الجمهور", width: "300px" },
    { id: "title", label: "العنوان", width: "200px" },
    { id: "status", label: "الحالة", width: "130px" },
    { id: "result", label: "النتيجة", width: "180px" },
    { id: "createdBy", label: "المشرف", width: "170px" },
    { id: "actions", label: "الاعدادات", width: "120px", pinned: true },
];

/** The explicit "every city" choice, so that an empty city is "not chosen yet". */
const ALL_CITIES = "all";

const valueOption = (options, value) => options.find((option) => option.value === value) || null;

// Numbers only, 24 hour, the same shape `utils/orderTiming.js` already uses.
// `dateStyle: "medium"` wrote an English month name into an Arabic page, and
// an Arabic locale put a "م"/"ص" marker inside a left-to-right run, which the
// browser then reordered into something unreadable.
const formatDate = (value) => formatDateTime(value) ?? "—";

/**
 * The second line of a campaign's «النتيجة» cell.
 *
 * ACCOUNTS AND DEVICES ARE DIFFERENT UNITS. One account can have several
 * devices, so the old «30 من 29» (accepted devices of accounts) read as more
 * than all of them. The first line now counts accounts on both sides, and this
 * line gives the devices, each number with its own unit.
 *
 * @param {{acceptedTokens: number, noDeviceUsers: number, failedTokens: number, prunedTokens: number}} campaign
 * @returns {string}
 */
const campaignResultNote = (campaign) => {
    const failed = (campaign.failedTokens || 0) + (campaign.prunedTokens || 0);
    return [
        `${numberFormatter.format(campaign.acceptedTokens || 0)} جهازًا استلم`,
        campaign.noDeviceUsers > 0
            ? `${numberFormatter.format(campaign.noDeviceUsers)} بدون جهاز`
            : null,
        failed > 0 ? `${numberFormatter.format(failed)} جهازًا فشل` : null,
    ]
        .filter(Boolean)
        .join(" · ");
};

/**
 * The home page's stat card, reused.
 *
 * `components/HomePage/HomePage.js` declares the same three parts — an icon
 * badge, a value and a label — and `home.scss` styles them. The campaign
 * screens carried their own boxes, which is how a second look for the same
 * thing starts.
 */
export const CampaignStat = ({ tone, icon, value, label }) => (
    <article className={`home-stat${tone ? ` home-stat--${tone}` : ""}`}>
        <div className="home-stat__icon">{icon}</div>
        <div className="home-stat__content">
            <span className="home-stat__value">{numberFormatter.format(value)}</span>
            <span className="home-stat__label">{label}</span>
        </div>
    </article>
);

const ReachSummary = ({ reach }) => (
    <div className="campaign-confirm__reach">
        <span>سيصل إلى</span>
        <bdi dir="ltr">{numberFormatter.format(reach.reachable)}</bdi>
        <span>من أصل</span>
        <bdi dir="ltr">{numberFormatter.format(reach.users)}</bdi>
    </div>
);

const SendNotification = () => {
    const toast = useRef(null);
    const { cities } = useCity();
    const adminInfo = useSelector((state) => state.auth.userData);
    const isSuperAdmin = adminInfo?.super_admin === true;
    const visibleCities = useMemo(
        () =>
            isSuperAdmin
                ? (cities ?? [])
                : (cities ?? []).filter((city) => String(city.id) === String(adminInfo?.cityId)),
        [adminInfo?.cityId, cities, isSuperAdmin],
    );

    const [targetMode, setTargetMode] = useState("audience");
    const [role, setRole] = useState("customer");
    const [cityId, setCityId] = useState("");
    const [activity, setActivity] = useState("all");
    const [activityDays, setActivityDays] = useState(30);
    const [userSearch, setUserSearch] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [campaignPage, setCampaignPage] = useState(1);

    const effectiveCityId =
        cityId || (!isSuperAdmin && visibleCities[0] ? String(visibleCities[0].id) : "");

    // NO CITY IS CHOSEN FOR A SUPER ADMIN. The page used to open with "كل المدن"
    // already selected, so it computed a reach of every customer in the country
    // and one click on «مراجعة وإرسال» stood between an admin and a broadcast to
    // all of them. Saher asked for the opposite («شيلي خيار ارسال اشعار للكل»),
    // so the whole platform is a deliberate choice now, not the default.
    // A city admin has exactly one city, so it stays selected for them.
    const cityOptions = [
        { value: "", label: isSuperAdmin ? "اختر المدينة" : "لا توجد مدينة مرتبطة بالحساب" },
        ...visibleCities.map((city) => ({ value: String(city.id), label: city.name })),
        ...(isSuperAdmin ? [{ value: ALL_CITIES, label: "كل المدن" }] : []),
    ];
    const userSearchReady = targetMode === "user" && userSearch.trim().length >= 2;
    const { users, loading: usersLoading } = useGetUsersDropdown(
        {
            role,
            cityId: effectiveCityId || undefined,
            search: userSearch.trim(),
        },
        { enabled: userSearchReady },
    );
    const userOptions = users.map((user) => ({
        value: user.id,
        label: `${user.name} (#${user.id})`,
    }));

    const target = useMemo(() => {
        if (targetMode === "user") {
            return selectedUser ? { userId: selectedUser.value } : null;
        }
        // No city chosen yet: nothing to preview and nothing to send.
        if (!effectiveCityId) return null;
        const days = Number(activityDays);
        return {
            role,
            ...(effectiveCityId === ALL_CITIES ? {} : { cityId: Number(effectiveCityId) }),
            activity,
            // A cleared days field used to send activityDays=0, which the API
            // rejected with an English validation message.
            ...(activity === "ordered_within" && days > 0 ? { activityDays: days } : {}),
        };
    }, [activity, activityDays, effectiveCityId, role, selectedUser, targetMode]);

    const {
        reach,
        loading: reachLoading,
        error: reachError,
    } = useNotificationCampaignReach(target, Boolean(target));
    const {
        campaigns,
        totalPages,
        loading: campaignsLoading,
    } = useNotificationCampaigns({
        page: campaignPage,
        size: 10,
    });
    const createCampaign = useCreateNotificationCampaign();
    const { orderedColumns, visibleColumns, hiddenSet, toggleColumn, moveColumn, reset } =
        useTableColumnsConfig("notification-campaigns", CAMPAIGN_COLUMNS);

    const showMessage = (severity, summary, detail) => {
        toast.current?.show({ severity, summary, detail, life: 4000 });
    };

    const requestConfirmation = (event) => {
        event.preventDefault();
        if (!target || !reach || !title.trim() || !description.trim()) {
            showMessage("warn", "أكمل البيانات", "اختر الجمهور واكتب العنوان والنص");
            return;
        }
        setShowConfirm(true);
    };

    const confirmSend = async () => {
        try {
            const result = await createCampaign.mutateAsync({
                title: title.trim(),
                description: description.trim(),
                target,
            });
            setShowConfirm(false);
            setTitle("");
            setDescription("");
            showMessage("success", "بدأ الإرسال", `رقم الحملة ${result.campaignId}`);
        } catch (error) {
            showMessage(
                "error",
                "تعذر الإرسال",
                error?.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <div className="campaign-page">
            <Toast style={{ direction: "rtl" }} ref={toast} position="top-left" />

            <section className="campaign-card campaign-compose">
                {/* The heading belongs INSIDE the card, the way the home page's
                    sections and the complaints list do it. */}
                <div className="campaign-card__heading">
                    <h3>إرسال إشعار</h3>
                    <span className="campaign-card__hint">الجمهور من بيانات الحسابات الحالية</span>
                </div>

                <form onSubmit={requestConfirmation}>
                    <h4 className="campaign-compose__step">من يصله الإشعار</h4>
                    <div className="campaign-compose__fields">
                        <label>
                            <span>طريقة تحديد الجمهور</span>
                            <SelectField
                                value={valueOption(MODE_OPTIONS, targetMode)}
                                options={MODE_OPTIONS}
                                onChange={(option) => {
                                    setTargetMode(option.value);
                                    setSelectedUser(null);
                                }}
                            />
                        </label>

                        <label>
                            <span>نوع الحساب</span>
                            <SelectField
                                value={valueOption(ROLE_OPTIONS, role)}
                                options={ROLE_OPTIONS}
                                onChange={(option) => {
                                    setRole(option.value);
                                    setSelectedUser(null);
                                }}
                            />
                        </label>

                        <label>
                            <span>المدينة</span>
                            <SelectField
                                value={valueOption(cityOptions, effectiveCityId)}
                                options={cityOptions}
                                onChange={(option) => {
                                    setCityId(option?.value ?? "");
                                    setSelectedUser(null);
                                }}
                            />
                        </label>

                        {targetMode === "audience" ? (
                            <>
                                <label>
                                    <span>سجل الطلبات</span>
                                    <SelectField
                                        value={valueOption(ACTIVITY_OPTIONS, activity)}
                                        options={ACTIVITY_OPTIONS}
                                        onChange={(option) => setActivity(option.value)}
                                    />
                                </label>
                                {activity === "ordered_within" ? (
                                    <label>
                                        <span>عدد الأيام</span>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="3650"
                                            value={activityDays}
                                            onChange={(event) =>
                                                setActivityDays(event.target.value)
                                            }
                                            required
                                        />
                                    </label>
                                ) : null}
                            </>
                        ) : (
                            <label>
                                <span>الحساب</span>
                                <SelectField
                                    value={selectedUser}
                                    options={userOptions}
                                    isLoading={usersLoading}
                                    isSearchable
                                    isClearable
                                    inputValue={userSearch}
                                    onInputChange={(value, meta) => {
                                        // `input-change` is typing. The other
                                        // actions — `set-value`, `input-blur`,
                                        // `menu-close` — are react-select
                                        // asking to CLEAR the box, and ignoring
                                        // them left the search text sitting in
                                        // a control with nothing selected: the
                                        // field read as filled while the target
                                        // was still empty.
                                        if (meta.action === "input-change") {
                                            setUserSearch(value);
                                            setSelectedUser(null);
                                            return value;
                                        }
                                        setUserSearch("");
                                        return "";
                                    }}
                                    onChange={(option) => {
                                        setSelectedUser(option);
                                        setUserSearch("");
                                    }}
                                    loadingMessage={() => "جارٍ البحث..."}
                                    noOptionsMessage={() =>
                                        userSearchReady ? "لا توجد حسابات" : "اكتب حرفين على الأقل"
                                    }
                                    placeholder="ابحث عن حساب بالاسم أو الرقم"
                                />
                            </label>
                        )}
                    </div>

                    <div className="campaign-reach" aria-live="polite">
                        {reachLoading ? (
                            <div className="campaign-reach__state">
                                <Ring size={26} lineWeight={5} speed={2} color={BRAND} />
                                <span>جارٍ حساب الجمهور...</span>
                            </div>
                        ) : reachError ? (
                            <div className="campaign-reach__state campaign-reach__state--error">
                                تعذر حساب الجمهور. حاول مرة أخرى.
                            </div>
                        ) : reach ? (
                            <div className="home-stats-grid">
                                <CampaignStat
                                    icon={<FontAwesomeIcon icon={faUsers} />}
                                    value={reach.users}
                                    label="الحسابات المستهدفة"
                                />
                                <CampaignStat
                                    tone="money"
                                    icon={<FontAwesomeIcon icon={faMobileScreenButton} />}
                                    value={reach.reachable}
                                    label="لديهم جهاز مسجل"
                                />
                                <CampaignStat
                                    icon={<FontAwesomeIcon icon={faPaperPlane} />}
                                    value={reach.tokens}
                                    label="الأجهزة المسجلة"
                                />
                                <CampaignStat
                                    tone="deleted"
                                    icon={<FontAwesomeIcon icon={faUserSlash} />}
                                    value={reach.noDevice}
                                    label="بدون جهاز مسجل"
                                />
                            </div>
                        ) : (
                            <div className="campaign-reach__state">
                                اختر الجمهور لعرض عدد الأجهزة
                            </div>
                        )}
                    </div>

                    <h4 className="campaign-compose__step">محتوى الإشعار</h4>
                    <div className="campaign-compose__message">
                        <label>
                            <span>العنوان</span>
                            <Form.Control
                                value={title}
                                maxLength={255}
                                onChange={(event) => setTitle(event.target.value)}
                                required
                            />
                        </label>
                        <label>
                            <span>النص</span>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                value={description}
                                onChange={(event) => setDescription(event.target.value)}
                                required
                            />
                        </label>
                    </div>

                    <div className="campaign-compose__footer">
                        <p className="campaign-compose__footer-note">
                            {reach
                                ? `سيصل إلى ${numberFormatter.format(reach.reachable)} من أصل ${numberFormatter.format(reach.users)} حسابًا.`
                                : "اختر الجمهور أولًا."}
                        </p>
                        <button
                            className="button"
                            type="submit"
                            disabled={!reach || reach.users === 0 || createCampaign.isPending}
                        >
                            مراجعة وإرسال
                        </button>
                    </div>
                </form>
            </section>

            <section className="campaign-card campaign-history">
                <div className="campaign-card__heading">
                    <h3>سجل الإرسال</h3>
                    <div>
                        <TableColumnsMenu
                            orderedColumns={orderedColumns}
                            hiddenSet={hiddenSet}
                            toggleColumn={toggleColumn}
                            moveColumn={moveColumn}
                            reset={reset}
                        />
                    </div>
                </div>
                <div className="table-container">
                    <table>
                        <TableHead visibleColumns={visibleColumns} />
                        <tbody>
                            {campaignsLoading ? (
                                <tr>
                                    <td colSpan={visibleColumns.length}>جارٍ تحميل السجل...</td>
                                </tr>
                            ) : campaigns.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColumns.length}>
                                        <h4 className="campaign-history__empty">
                                            لا توجد حملات بعد
                                        </h4>
                                    </td>
                                </tr>
                            ) : (
                                campaigns.map((campaign) => {
                                    const cellRenderers = {
                                        id: () => <td>{campaign.id}</td>,
                                        createdAt: () => (
                                            <td>
                                                {/* A date is a left-to-right run
                                                    inside a right-to-left cell.
                                                    Without the isolation the
                                                    browser moved the time to
                                                    the wrong side of the
                                                    comma. */}
                                                <bdi dir="ltr">
                                                    {formatDate(campaign.createdAt)}
                                                </bdi>
                                            </td>
                                        ),
                                        target: () => (
                                            <td className="campaign-history__cell--wrap">
                                                <span>
                                                    {notificationTargetLabel(
                                                        campaign.target,
                                                        cities ?? [],
                                                    )}
                                                </span>
                                                {campaign.sourceCampaignId ? (
                                                    <span className="campaign-history__note">
                                                        {`إعادة إرسال للحملة #${campaign.sourceCampaignId}`}
                                                    </span>
                                                ) : null}
                                            </td>
                                        ),
                                        title: () => (
                                            <td className="campaign-history__cell--wrap">
                                                <bdi>{campaign.title}</bdi>
                                            </td>
                                        ),
                                        status: () => (
                                            <td>
                                                <span
                                                    className={`campaign-status campaign-status--${campaign.status}`}
                                                >
                                                    {notificationStatusLabel(campaign.status)}
                                                </span>
                                            </td>
                                        ),
                                        result: () => (
                                            <td className="campaign-history__cell--wrap">
                                                <span className="campaign-history__figure">
                                                    {`${numberFormatter.format(campaign.reachableUsers)} من ${numberFormatter.format(campaign.totalUsers)} حسابًا`}
                                                </span>
                                                <span className="campaign-history__note">
                                                    {campaignResultNote(campaign)}
                                                </span>
                                            </td>
                                        ),
                                        createdBy: () => <td>{campaign.createdBy?.name || "—"}</td>,
                                        actions: () => (
                                            <td>
                                                <NavLink
                                                    to={`/send-notification/${campaign.id}`}
                                                    className="button button--quiet campaign-history__action"
                                                >
                                                    التفاصيل
                                                </NavLink>
                                            </td>
                                        ),
                                    };
                                    return (
                                        <tr className="item" key={campaign.id}>
                                            <TableBodyCells
                                                visibleColumns={visibleColumns}
                                                renderers={cellRenderers}
                                            />
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                {totalPages > 1 ? (
                    <div className="campaign-page__pagination">
                        <Pagination
                            count={totalPages}
                            page={campaignPage}
                            onChange={(_event, page) => setCampaignPage(page)}
                            color="primary"
                            shape="rounded"
                        />
                    </div>
                ) : null}
            </section>

            {showConfirm ? (
                <div
                    className="campaign-confirm"
                    role="presentation"
                    onClick={() => setShowConfirm(false)}
                >
                    <div
                        className="surface-card campaign-confirm__dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="campaign-confirm-title"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <h2 id="campaign-confirm-title" className="section-title">
                            تأكيد الإرسال
                        </h2>
                        <p>{notificationTargetLabel(target, cities ?? [], selectedUser?.label)}</p>
                        <ReachSummary reach={reach} />
                        <div className="surface-inset campaign-confirm__message">
                            <span className="campaign-confirm__label">العنوان</span>
                            {/* Isolated, not re-directed — see the detail page. */}
                            <h3>
                                <bdi>{title}</bdi>
                            </h3>
                            <span className="campaign-confirm__label">النص</span>
                            <p>
                                <bdi>{description}</bdi>
                            </p>
                        </div>
                        <div className="form-warning">لا يمكن سحب الإشعار بعد بدء الإرسال.</div>
                        <div className="campaign-confirm__actions">
                            <button
                                type="button"
                                className="button"
                                onClick={confirmSend}
                                disabled={createCampaign.isPending}
                            >
                                تأكيد الإرسال
                            </button>
                            <button
                                type="button"
                                className="button button--quiet"
                                onClick={() => setShowConfirm(false)}
                            >
                                إلغاء
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default SendNotification;
